import { useEffect, useState } from 'react'
import { useEventSettings, useUpdateEventSettings } from '@/lib/useEventSettings'
import { useToastStore } from '@/stores/toast'
import type { EventCategory, EventSettingsUpdate } from '@/types/eventSettings'

const CATEGORY_LABELS: Record<EventCategory, string> = {
  interest_rate: 'Zinsentscheidungen',
  inflation: 'Inflation',
  employment: 'Arbeitsmarkt',
  growth: 'Wachstum/BIP',
  other: 'Sonstige',
}

const ALL_CATEGORIES: EventCategory[] = [
  'interest_rate',
  'inflation',
  'employment',
  'growth',
  'other',
]

export function EventSettingsForm() {
  const query = useEventSettings()
  const mutation = useUpdateEventSettings()
  const pushToast = useToastStore((s) => s.pushToast)

  const [categories, setCategories] = useState<Set<EventCategory>>(new Set())
  const [earningsOn, setEarningsOn] = useState(true)
  const [weightPct, setWeightPct] = useState(5)

  useEffect(() => {
    if (query.data) {
      setCategories(new Set(query.data.alert_categories))
      setEarningsOn(query.data.earnings_alert_enabled)
      setWeightPct(Number(query.data.earnings_min_weight_pct) * 100)
    }
  }, [query.data])

  if (query.isLoading) {
    return <p className="font-mono text-sm text-muted">Lade Einstellungen …</p>
  }

  const weightError =
    !Number.isFinite(weightPct) || weightPct < 0 || weightPct > 100
      ? 'Wert muss zwischen 0 und 100 % liegen'
      : null

  const canSubmit = !weightError && !mutation.isPending

  function toggleCategory(cat: EventCategory) {
    setCategories((prev) => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }

  function handleSave() {
    if (!canSubmit) return
    const patch: EventSettingsUpdate = {
      alert_categories: [...categories],
      earnings_alert_enabled: earningsOn,
      earnings_min_weight_pct: (weightPct / 100).toString(),
    }
    mutation.mutate(patch, {
      onSuccess: () => pushToast({ kind: 'success', message: 'Event-Einstellungen gespeichert' }),
      onError: (err) =>
        pushToast({
          kind: 'error',
          message: 'Speichern fehlgeschlagen',
          detail: (err as Error).message,
        }),
    })
  }

  return (
    <div className="border border-hairline bg-panel px-6 py-6">
      <fieldset>
        <legend className="tracked-caps text-[10px] text-bernstein">Makro-Alerts</legend>
        <div className="mt-3 grid gap-2">
          {ALL_CATEGORIES.map((cat) => (
            <label key={cat} className="flex items-center gap-3 text-ink">
              <input
                type="checkbox"
                aria-label={CATEGORY_LABELS[cat]}
                checked={categories.has(cat)}
                onChange={() => toggleCategory(cat)}
              />
              {CATEGORY_LABELS[cat]}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-6 border-t border-hairline pt-4">
        <label className="flex items-center gap-3 text-ink">
          <input
            type="checkbox"
            aria-label="Earnings-Alerts aktiv"
            checked={earningsOn}
            onChange={(e) => setEarningsOn(e.target.checked)}
          />
          Earnings-Alerts (24h vorher)
        </label>
        <div className="mt-3 grid grid-cols-[1fr_120px] items-center gap-3">
          <div>
            <label htmlFor="earnings-weight" className="text-ink">
              Mindest-Gewicht
            </label>
            <p className="font-mono text-xs text-muted">nur Positionen ≥ X % des Portfolios</p>
            {weightError && (
              <p role="alert" className="mt-1 font-mono text-xs text-loss">
                {weightError}
              </p>
            )}
          </div>
          <input
            id="earnings-weight"
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            step={1}
            value={weightPct}
            onChange={(e) => setWeightPct(Number(e.target.value))}
            className="w-full border border-hairline bg-void px-3 py-2 text-right font-mono text-sm text-ink transition-colors focus:border-bernstein focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          disabled={!canSubmit}
          onClick={handleSave}
          className="tracked-caps bg-bernstein px-6 py-3 text-[11px] text-void transition-colors hover:bg-bernstein-dim disabled:cursor-not-allowed disabled:bg-faint disabled:text-void/60"
        >
          Speichern
        </button>
      </div>

      {mutation.isError && (
        <p role="alert" className="mt-3 font-mono text-xs text-loss">
          Speichern fehlgeschlagen: {(mutation.error as Error).message}
        </p>
      )}
    </div>
  )
}
